/**
 * Bundled preset avatars for attendee profiles.
 *
 * Kept dependency-free: each preset is a small inline SVG (a colored gradient
 * disc with a simple motif) rendered as a data URI, so there are no binary
 * assets to ship and they render anywhere (web, native WebView, email). The
 * preset ids must stay in sync with services/person_profile.PRESET_AVATAR_IDS
 * on the backend (preset-1 … preset-8).
 */

export interface AvatarPreset {
  id: string
  label: string
  colors: [string, string]
}

// Brand-adjacent palette (docs/vericast-events-brand-guide.md).
export const AVATAR_PRESETS: AvatarPreset[] = [
  { id: 'preset-1', label: 'Ocean', colors: ['#0075BC', '#1C5580'] },
  { id: 'preset-2', label: 'Teal', colors: ['#5BC6CC', '#0075BC'] },
  { id: 'preset-3', label: 'Sunset', colors: ['#E47720', '#C0392B'] },
  { id: 'preset-4', label: 'Grape', colors: ['#7A5AA6', '#1C5580'] },
  { id: 'preset-5', label: 'Forest', colors: ['#2E8B57', '#5BC6CC'] },
  { id: 'preset-6', label: 'Slate', colors: ['#2C3E50', '#51788F'] },
  { id: 'preset-7', label: 'Coral', colors: ['#E47720', '#7A5AA6'] },
  { id: 'preset-8', label: 'Sky', colors: ['#5BC6CC', '#2C3E50'] },
]

/** A data-URI SVG for a preset (a gradient disc with a soft highlight). */
export function presetAvatarUrl(id: string): string {
  const p = AVATAR_PRESETS.find((a) => a.id === id) ?? AVATAR_PRESETS[0]
  const [c1, c2] = p.colors
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="128" height="128" viewBox="0 0 128 128">
    <defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0" stop-color="${c1}"/><stop offset="1" stop-color="${c2}"/>
    </linearGradient></defs>
    <circle cx="64" cy="64" r="64" fill="url(#g)"/>
    <circle cx="64" cy="50" r="22" fill="rgba(255,255,255,0.9)"/>
    <path d="M64 78c-24 0-40 14-40 34v16h80v-16c0-20-16-34-40-34z" fill="rgba(255,255,255,0.9)"/>
  </svg>`
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`
}
